import { Mesh } from 'three/src/objects/Mesh';
import {
  PourDto,
  PourShapeDto,
  PourShapesExportDto,
  PourStatus
} from 'providers/api';
import {
  defaultFillAnchor,
  sortPoursChronologically,
  walkStopeFill
} from 'modules/project/StopeFillPage/Three/pour';
import { StopeSourceBounds } from 'modules/project/StopeFillPage/Three/useStopeModel';

const getFiniteNumber = (value: number | undefined | null): number | undefined => (
  typeof value === 'number' && Number.isFinite(value) ? value : undefined
);

const formatNumber = (value: number | undefined): string | undefined => (
  value === undefined ? undefined : String(Math.round((value + Number.EPSILON) * 1000) / 1000)
);

const binderTailingsRatioToPercentage = (ratio: number | undefined): number | undefined => (
  ratio === undefined ? undefined : (ratio / (1 + ratio)) * 100
);

// Same value precedence as the stope CSV export (actual completion data preferred over the design
// spec), so the DUF attributes agree with the flat Deswik data hand-off.
const resolveBinderPercentage = (pour: PourDto): number | undefined => (
  getFiniteNumber(pour.pourData?.binderContent)
  ?? binderTailingsRatioToPercentage(getFiniteNumber(pour.pourData?.binderTailingsRatio))
  ?? getFiniteNumber(pour.pasteSelectedSpecification?.binderContent)
  ?? binderTailingsRatioToPercentage(getFiniteNumber(pour.hydraulicSpecification?.binderTailingsRatio))
);

const resolveMassConcentration = (pour: PourDto): number | undefined => (
  getFiniteNumber(pour.pourData?.massConcentration)
  ?? getFiniteNumber(pour.pasteSelectedSpecification?.massConcentration)
  ?? getFiniteNumber(pour.hydraulicSelectedSpecification?.massConcentration)
);

const buildPourAttributes = (
  pour: PourDto,
  stopeName: string,
  solidVolume: number,
): { [key: string]: string } => {
  const attributes: { [key: string]: string } = {};
  const setAttribute = (name: string, value: string | undefined) => {
    if (value !== undefined && value !== '') attributes[name] = value;
  };

  setAttribute('BFP_STOPE', stopeName);
  setAttribute('BFP_POUR', pour.reference);
  setAttribute('BFP_STATUS', PourStatus[pour.status]);
  setAttribute('BFP_FILL_TYPE', pour.isRockFill ? 'Rock Fill' : 'Backfill');
  setAttribute('BFP_INTENDED_POUR_DATE', pour.intendedPourDate?.toString());
  setAttribute('BFP_RECIPE', pour.recipeReference);
  setAttribute('BFP_RHEOLOGY_DATASET', pour.rheologyDataSetReference);
  setAttribute('BFP_DESIGN_VOLUME_M3', formatNumber(getFiniteNumber(pour.volume)));
  setAttribute('BFP_POUR_HEIGHT_M', formatNumber(getFiniteNumber(pour.pourHeight)));
  setAttribute('BFP_SOLID_VOLUME_M3', formatNumber(getFiniteNumber(solidVolume)));
  setAttribute('BFP_BINDER_PERCENTAGE', formatNumber(resolveBinderPercentage(pour)));
  setAttribute('BFP_MASS_CONCENTRATION', formatNumber(resolveMassConcentration(pour)));
  setAttribute('BFP_BINDER_TONNES', formatNumber(getFiniteNumber(pour.pourData?.totalBinder)));

  // Completion details only exist once the pour is complete; referencing stays possible in Deswik
  // via BFP_STOPE + BFP_POUR on a re-export after completion.
  setAttribute('BFP_COMPLETED_DATE', pour.pourData?.pourCompletedDate?.toString());
  setAttribute('BFP_ACTUAL_VOLUME_M3', formatNumber(getFiniteNumber(pour.pourData?.volume)));
  setAttribute('BFP_DRY_TONNAGE_T', formatNumber(getFiniteNumber(pour.pourData?.dryTonnage)));
  setAttribute('BFP_UCS_AVERAGE', formatNumber(getFiniteNumber(pour.pourData?.ucsAverage)));
  setAttribute('BFP_COMPLETION_COMMENTS', pour.pourData?.comments);

  return attributes;
};

// Convert a carved fill solid (local view frame: rotated Z-up -> Y-up, bbox centred in X/Z, base at
// y=0) into flat mine-grid vertex/triangle arrays, welding duplicate vertices so the polyface is a
// connected mesh rather than a triangle soup.
const buildShapeGeometry = (
  solid: Mesh,
  bounds: StopeSourceBounds,
): { vertices: number[]; triangles: number[] } | null => {
  const { geometry } = solid;
  const positions = geometry.getAttribute('position');
  if (!positions || positions.count < 3) return null;

  const vertices: number[] = [];
  const triangles: number[] = [];
  const weldedIndexByKey = new Map<string, number>();
  const weldedIndexByPosition: number[] = new Array(positions.count);

  for (let i = 0; i < positions.count; i += 1) {
    const x = positions.getX(i);
    const y = positions.getY(i);
    const z = positions.getZ(i);
    const key = `${x},${y},${z}`;
    let welded = weldedIndexByKey.get(key);
    if (welded === undefined) {
      welded = vertices.length / 3;
      weldedIndexByKey.set(key, welded);
      // Undo the view transform: local (x, y, z) -> mine grid (x + cx, cy - z, y + minZ).
      vertices.push(x + bounds.centerX, bounds.centerY - z, y + bounds.minZ);
    }
    weldedIndexByPosition[i] = welded;
  }

  const index = geometry.getIndex();
  const triangleCount = index ? index.count / 3 : positions.count / 3;
  for (let i = 0; i < triangleCount; i += 1) {
    const a = weldedIndexByPosition[index ? index.getX(i * 3) : i * 3];
    const b = weldedIndexByPosition[index ? index.getX(i * 3 + 1) : i * 3 + 1];
    const c = weldedIndexByPosition[index ? index.getX(i * 3 + 2) : i * 3 + 2];
    // Welding can collapse degenerate triangles; drop them.
    if (a !== b && b !== c && a !== c) {
      triangles.push(a, b, c);
    }
  }

  return triangles.length >= 3 ? { vertices, triangles } : null;
};

// Rebuild the stope fill exactly as the view renders it (same stacking kernel, same default anchor:
// the transformed stope's bbox centre) and package each slice as a mine-grid mesh with the pour's
// data as attributes. BFP_SOLID_VOLUME_M3 carries the exact integral's slice volume rather than the CSG
// volume of the exported shell, so it agrees with what the app reports for the pour.
const buildPourSolidsExport = (
  originalStope: Mesh,
  bounds: StopeSourceBounds,
  pours: PourDto[],
  stopeName: string,
): PourShapesExportDto | null => {
  if (pours.length === 0) return null;

  const defaultAnchor = defaultFillAnchor(originalStope);
  const shapes: PourShapeDto[] = [];

  walkStopeFill(originalStope, sortPoursChronologically(pours), defaultAnchor, (step) => {
    const shapeGeometry = buildShapeGeometry(step.fillSolid, bounds);
    if (!shapeGeometry) return;
    shapes.push({
      name: step.pour.reference || `Pour ${step.index + 1}`,
      vertices: shapeGeometry.vertices,
      triangles: shapeGeometry.triangles,
      attributes: buildPourAttributes(step.pour, stopeName, step.sliceVolume),
    });
  });

  return shapes.length > 0 ? { stopeName, shapes } : null;
};

export default buildPourSolidsExport;
